import { parse } from '@conform-to/zod';
import { GoogleOAuthProvider, useGoogleOneTapLogin } from '@react-oauth/google';
import { cssBundleHref } from '@remix-run/css-bundle';
import {
  json,
  redirect,
  type DataFunctionArgs,
  type HeadersFunction,
  type LinksFunction,
  type MetaFunction } from
'@remix-run/node';
import {
  Link,
  Links,
  LiveReload,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useFetchers,
  useLoaderData,
  useLocation,
  useSubmit } from
'@remix-run/react';
import { withSentry } from '@sentry/remix';
import { jwtDecode } from 'jwt-decode';
import { Check, InfoIcon } from "lucide-react";
import { useEffect, useState } from 'react';
import { AuthenticityTokenProvider } from 'remix-utils/csrf/react';
import { HoneypotProvider } from 'remix-utils/honeypot/react';
import { z } from 'zod';
// import GooglePlayImg from '#app/assets/googleplay3.png'
import SuperFDLogo from '#app/assets/logo3_superfd.png';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuItem,
  DropdownMenuContent,
  DropdownMenuPortal } from
'#components/ui/dropdown-menu.tsx';
import { GeneralErrorBoundary } from './components/error-boundary.tsx';
import { EpicProgress } from './components/progress-bar.tsx';
import { EpicToaster } from './components/toaster.tsx';
import { Button } from './components/ui/button.tsx';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './components/ui/dialog.tsx';
import { Icon, href as iconsHref } from './components/ui/icon.tsx';
import { Separator } from './components/ui/separator.tsx';
import tailwindStyleSheetUrl from './styles/tailwind.css';
import { getGmailUserId, getUserId, gmailOneTapSignup, sessionKey } from './utils/auth.server.ts';
import { ClientHintCheck, getHints } from './utils/client-hints.tsx';
import { csrf } from './utils/csrf.server.ts';
import { prisma } from './utils/db.server.ts';
import { getEnv, getTarrakkiEnv } from './utils/env.server.ts';
import { gmailSessionKey, gmailSessionStorage } from './utils/gmailSession.server.ts';
import { honeypot } from './utils/honeypot.server.ts';
import { isSeniorSessionKey, isSeniorSessionStorage } from './utils/isSeniorSession.server.ts';
import { combineHeaders, getDomainUrl, getSourceDeviceType, getUserImgSrc } from './utils/misc.tsx';
import { useNonce } from './utils/nonce-provider.ts';
import { userHasRole } from './utils/permissions.ts';
import { getProfileImageFromStorage } from './utils/platforms/decentro.server.ts';
import { useRequestInfo } from './utils/request-info.ts';
import { authSessionStorage } from './utils/session.server.ts';
import { type Theme, getTheme } from './utils/theme.server.ts';
import { makeTimings } from './utils/timing.server.ts';
import { getToast } from './utils/toast.server.ts';
import { useOptionalGmailUser, useOptionalUser } from './utils/user.ts';

const DEFAULT_THEME = 'dark';
const IS_SENIOR_FORM_ID = 'is-senior-form';

// const LINKS = [
// 	{ name: 'Home', to: '/' },
// 	{ name: 'About Us', to: '/about' },
// 	{ name: 'FD Calculator', to: '/calculator' }
// ]

export const links: LinksFunction = () => {
  return [
  // Preload svg sprite as a resource to avoid render blocking
  { rel: 'preload', href: iconsHref, as: 'image' },
  // Preload CSS as a resource to avoid render blocking
  { rel: 'preload', href: tailwindStyleSheetUrl, as: 'style' },
  cssBundleHref ? { rel: 'preload', href: cssBundleHref, as: 'style' } : null,
  { rel: 'mask-icon', href: '/favicons/mask-icon.svg' },
  {
    rel: 'alternate icon',
    type: 'image/png',
    href: '/favicons/favicon-32x32.png'
  },
  { rel: 'apple-touch-icon', href: '/favicons/apple-touch-icon.png' }, (
  {
    rel: 'manifest',
    href: '/site.webmanifest',
    crossOrigin: 'use-credentials'
  } as const), // necessary to make typescript happy
  //These should match the css preloads above to avoid css as render blocking resource
  { rel: 'icon', type: 'image/svg+xml', href: '/favicons/favicon.svg' },
  { rel: 'stylesheet', href: tailwindStyleSheetUrl },
  cssBundleHref ? { rel: 'stylesheet', href: cssBundleHref } : null].
  filter(Boolean);
};

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  return [
  { title: data ? 'SuperFD' : 'Error | SuperFD' },
  { name: 'description', content: `Earn 9.85% interest rate on your next FD` }];

};

export async function loader({ request }: DataFunctionArgs) {

  const { toast, headers: toastHeaders } = await getToast(request);
  const honeyProps = honeypot.getInputProps();
  const [csrfToken, csrfCookieHeader] = await csrf.commitToken();
  const timings = makeTimings('root loader');

  const isSeniorSession = await isSeniorSessionStorage.getSession(
    request.headers.get('cookie')
  );
  const isSenior = isSeniorSession.get(isSeniorSessionKey);

  const userId = await getUserId(request);
  const gmailUserId = await getGmailUserId(request);
  const user = userId ?
  await prisma.user.findUniqueOrThrow({
    select: {
      id: true,
      name: true,
      age: true,
      isOnBoard: true,
      isProfileUploaded: true,
      profileImageType: true,
      image: {
        select: {
          id: true
        }
      },
      roles: {
        select: {
          name: true,
          permissions: {
            select: { entity: true, action: true, access: true }
          }
        }
      }
    },
    where: { id: userId }
  }) :
  null;

  const gmailUser = gmailUserId ?
  await prisma.gmailUser.findUnique({
    select: {
      id: true,
      name: true,
      givenName: true,
      email: true,
      picture: true
    },
    where: { id: gmailUserId }

  }) :
  null;

  let profileUrl = '';
  if (user && user.isProfileUploaded && user.profileImageType) {
    const { data } = await getProfileImageFromStorage(user.id, user.profileImageType);

    if (data && data.signedUrl) {
      profileUrl = data.signedUrl;
    }

  }

  const bannerCampaign = await prisma.marketingCampaign.findFirst({
    where: {
      AND: [
      { isActive: true },
      { type: 'banner' }]

    },
    select: {
      id: true,
      name: true,
      description: true,
      amount: true,
      type: true,
      startDateTime: true,
      endDateTime: true,
      isActive: true,
      remark: true
    }
  });

  let isBannerCampaignActive = false;
  if (bannerCampaign && bannerCampaign.startDateTime && bannerCampaign.endDateTime) {
    let startUtcTime = new Date(bannerCampaign.startDateTime).getTime();
    let endUtcTime = new Date(bannerCampaign.endDateTime).getTime();

    if (startUtcTime < Date.now() && Date.now() < endUtcTime) {
      isBannerCampaignActive = true;
    } else {
      isBannerCampaignActive = false;
    }

  }


  return json(
    {
      user,
      isSenior,
      gmailUser,
      bannerCampaign: {
        isActive: isBannerCampaignActive,
        description: bannerCampaign?.description,
        amount: bannerCampaign?.amount
      },
      profileImageUrl: profileUrl,
      requestInfo: {
        hints: getHints(request),
        origin: getDomainUrl(request),
        path: new URL(request.url).pathname,
        userPrefs: {
          theme: getTheme(request) ?? DEFAULT_THEME
        },
        deviceType: getSourceDeviceType(request)
      },
      ENV: getEnv(),
      tarrakkiENV: getTarrakkiEnv(),
      toast,
      honeyProps,
      csrfToken
    },
    {
      headers: combineHeaders(
        { 'Server-Timing': timings.toString() },
        toastHeaders,
        csrfCookieHeader ? { 'set-cookie': csrfCookieHeader } : null
      )
    }
  );
}

export const headers: HeadersFunction = ({ loaderHeaders }) => {
  const headers = {
    'Server-Timing': loaderHeaders.get('Server-Timing') ?? ''
  };
  return headers;
};

// const ThemeFormSchema = z.object({
// 	theme: z.enum(['system', 'light', 'dark']),
// })

const ThemeFormSchemaLightOrDark = z.object({
  theme: z.enum(['light', 'dark'])
});

export async function action({ request }: DataFunctionArgs) {

  if (request.headers.get("Content-Type") === 'application/json') {
    const jsonData = (await request.json() as {payloadData: string;});
    const parsedData = (JSON.parse(jsonData.payloadData) as {email: string;name: string;given_name: string;family_name: string;picture: string;});

    // Process the JSON data
    //console.log("Received JSON data:", parsedData);

    const deviceInfo = `${getSourceDeviceType(request)} - ${request.headers.get('user-agent')}`;
    const { type, session } = await gmailOneTapSignup({
      email: parsedData.email,
      name: parsedData.name,
      givenName: parsedData.given_name,
      familyName: parsedData.family_name,
      picture: parsedData.picture,
      deviceInfo: deviceInfo
    });

    if (type === 'user') {
      const authSession = await authSessionStorage.getSession(
        request.headers.get('cookie')
      );
      authSession.set(sessionKey, session.id);

      return redirect(
        '/',
        {
          headers: {
            'set-cookie': await authSessionStorage.commitSession(authSession, {
              expires: session.expirationDate
            })
          }
        }
      );

    } else if (type === 'gmailUser') {
      const gmailSession = await gmailSessionStorage.getSession(
        request.headers.get('cookie')
      );
      gmailSession.set(gmailSessionKey, session.id);

      return redirect(
        '/',
        {
          headers: {
            'set-cookie': await gmailSessionStorage.commitSession(gmailSession, {
              expires: session.expirationDate
            })
          }
        }
      );
    } else {
      return null;
    }

  } else {
    const body = await request.formData();
    const formId = (body.get('formId') as string);
    const isSenior = (body.get('isSenior') as string);

    if (formId === IS_SENIOR_FORM_ID) {
      const isSeniorSession = await isSeniorSessionStorage.getSession(
        request.headers.get('cookie')
      );
      isSeniorSession.set(isSeniorSessionKey, isSenior);

      return redirect(
        '/',
        {
          headers: {
            'set-cookie': await isSeniorSessionStorage.commitSession(isSeniorSession)
          }
        }
      );
    }

  }



  return null;



  //const formData = await request.formData()

  //console.log('action run', formData, request.body)
  //await verifyGmailUserData(request, gmail)

  // const gmailSession = await gmailSessionStorage.getSession(
  // 	request.headers.get('cookie')
  // )
  // gmailSession.set(gmailSessionKey, gmail)

  // return redirect(
  // 	'/', 
  // 	{ 
  //       headers: {
  // 		'set-cookie': await gmailSessionStorage.commitSession(gmailSession),
  // 	  }
  // 	}
  // )


  // const formData = await request.formData()
  // const submission = parse(formData, {
  // 	schema: ThemeFormSchemaLightOrDark,
  // })
  // if (submission.intent !== 'submit') {
  // 	return json({ status: 'idle', submission } as const)
  // }
  // if (!submission.value) {
  // 	return json({ status: 'error', submission } as const, { status: 400 })
  // }
  // const { theme } = submission.value

  // const responseInit = {
  // 	headers: { 'set-cookie': setThemeLightOrDark(theme) },
  // }
  // return json({ success: true, submission }, responseInit)

}

function Document({
  children,
  nonce,
  theme = DEFAULT_THEME,
  env = {},
  tarrakkiEnv






}: {children: React.ReactNode;nonce: string;theme?: Theme;env?: Record<string, string>;tarrakkiEnv?: Record<string, string>;}) {
  return (
    <html lang="en" className={`${theme} h-full overflow-x-hidden`}>
			<head>
				<ClientHintCheck nonce={nonce} />
				<Meta />
				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width,initial-scale=1" />
				<Links />
			</head>
			<body className="bg-background text-foreground">
				{children}
				<script
          nonce={nonce}
          dangerouslySetInnerHTML={{
            __html: `window.ENV = ${JSON.stringify(env)}`
          }} />

				
				<ScrollRestoration nonce={nonce} />
				<Scripts nonce={nonce} />

				<script nonce={nonce} src={tarrakkiEnv ? tarrakkiEnv.TARRAKKI_SDK_URL : ''} />

				<script nonce={nonce} async src={tarrakkiEnv ? `https://www.googletagmanager.com/gtag/js?id=${tarrakkiEnv.GOOGLE_TAG_ID}` : ''} />

				{/* <script nonce={nonce} async src={`https://accounts.google.com/gsi/client`} /> */}

				<script
          nonce={nonce}
          dangerouslySetInnerHTML={{
            __html: `
					window.dataLayer = window.dataLayer || [];
					function gtag(){dataLayer.push(arguments);}
					gtag('js', new Date());

					gtag('config', '${tarrakkiEnv ? tarrakkiEnv.GOOGLE_TAG_ID : ''}', {
						page_path: window.location.pathname
					});
					`
          }} />

				{/* <script
          type="text/javascript"
          src="https://d3mkw6s8thqya7.cloudfront.net/integration-plugin.js"
          id="aisensy-wa-widget"
          widget-id="qJa6bV"
          >
          </script>
          */}

				{/* <script 
             nonce={nonce}
             dangerouslySetInnerHTML={{
          	__html: `
          	var url = 'https://wati-integration-prod-service.clare.ai/v2/watiWidget.js?90000';
          	var s = document.createElement('script');
          	s.type = 'text/javascript';
          	s.async = true;
          	s.src = url;
          	var options = {
          	"enabled":true,
          	"chatButtonSetting":{
          		"backgroundColor":"#00E785",
          		"ctaText":"Chat with SuperFD",
          		"borderRadius":"25",
          		"marginLeft": "0",
          		"marginRight": "20",
          		"marginBottom": "20",
          		"ctaIconWATI":false,
          		"position":"right"
          	},
          	"brandSetting":{
          		"brandName":"SuperFD",
          		"brandSubTitle":"undefined",
          		"brandImg":"https://www.superfd.in/",
          		"welcomeText":"Hi there!\n\nHow can we help you?",
          		"messageText":"%0A",
          		"backgroundColor":"#00E785",
          		"ctaText":"Chat with SuperFD",
          		"borderRadius":"25",
          		"autoShow":true,
          		"phoneNumber":"919902872525"
          	}
          	};
          	s.onload = function() {
          		console.log('creating whasapp chat widget')
          		CreateWhatsappChatWidget(options);
          	};
          	var x = document.getElementsByTagName('script')[0];
          	x.parentNode.insertBefore(s, x);
          	`,
          }}
          /> */}
				<LiveReload nonce={nonce} />
			</body>
		</html>);

}

function App() {
  const data = useLoaderData<typeof loader>();
  const location = useLocation();
  const submit = useSubmit();
  const nonce = useNonce();
  const user = useOptionalUser();
  const gmailUser = useOptionalGmailUser();
  const theme = useThemeLightOrDark();
  const userIsAdmin = userHasRole(user, 'admin');
  const [isSticky, setIsSticky] = useState(data.bannerCampaign.isActive ? false : true);
  const [isTermsOn, setIsTermsOn] = useState<boolean>(false);

  useGoogleOneTapLogin({
    onSuccess: async (credentialResponse) => {
      const { credential } = credentialResponse;
      const payload = credential ? jwtDecode(credential) : undefined;

      if (payload) {
        //console.log('users data : ', payload)
        submit(
          { payloadData: JSON.stringify(payload) },
          { method: "post", encType: "application/json" }
        );

      } else {
        console.log('payload is empty');
      }

      //console.log(credentialResponse);
    },
    onError: () => {
      console.log('Login Failed');
    },
    cancel_on_tap_outside: false,
    disabled: user && user?.id ? true : gmailUser && gmailUser.id ? true : false
  });

  // const initializeGSI = () => {
  // 	google.accounts.id.initialize({
  // 		client_id: '107276351695-67kqv1t3mdqs3c69ogpf4r5etfdud724.apps.googleusercontent.com',
  // 	});
  // 	google.accounts.id.prompt(notification => {
  // 		console.log(notification)
  // 	});
  // }

  useEffect(() => {

    // const script = document.createElement('script')
    // script.src = 'https://accounts.google.com/gsi/client'
    // script.onload = initializeGSI()
    // script.async = true;
    // document.querySelector('body').appendChild(script)

    const handleScroll = () => {
      if (data.bannerCampaign.isActive) {
        setIsSticky(window.scrollY > 40);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [data.bannerCampaign.isActive]);

  return (
    <Document nonce={nonce} theme={theme} env={data.ENV} tarrakkiEnv={data.tarrakkiENV}>
			{/* <div className="flex h-screen flex-col justify-between"> */}
				{/* <div className={`group ${isSticky ? 'hidden' : ''} relative top-0 bg-gradient-to-r from-violet-500 to-purple-500 py-2 text-white transition-all duration-300 md:py-0`}>
        <div className="container flex flex-col items-center justify-center py-2 px-2 gap-2 md:h-10 md:flex-row">
        <div
        className="inline-flex text-xs leading-normal md:text-sm"
        >
        {" "}
        <span className="mx-1 font-bold text-center">
            ✨{" "}
        	₹{data.bannerCampaign ? '1000' : '250'} Cashback on booking your first Bank FD
        	{" "}✨
        </span>
        {" "}
        {
        	data.bannerCampaign ? (
        		<button onClick={() => setIsTermsOn(!isTermsOn)}>
        		<InfoIcon className="w-4 h-4 ml-1 -mt-[3px] lg:inline-block" /> 
        		</button>
        	) : null
        }
        
        // <ChevronRight className="ml-1 -mt-[3px]  hidden transition-all duration-300 ease-out group-hover:translate-x-1 lg:inline-block" />
        </div>
        </div>
        <hr className="absolute bottom-0 m-0 h-px w-full bg-neutral-200/30" />
        </div> 8B1818 #15582a bg-gradient-to-r from-green-900 via-red-900 to-green-900 */}

				{
      data.bannerCampaign.isActive &&
      <div className={`group ${isSticky ? 'hidden' : ''} relative top-0 bg-[#15582a]  py-2 text-white overflow-hidden md:py-0`}>
						<div className="container flex items-center justify-center py-1 md:py-2 px-1 md:px-2 gap-2 md:h-10 md:flex-row">

							<div>
								<span className="mx-1 ml-0 md:ml-4 text-xs leading-normal md:text-sm font-bold text-center">
									{/* ₹{data.bannerCampaign ? '1000' : '250'} Cashback on booking your first Bank FD */}
									{data.bannerCampaign.description && data.bannerCampaign.description !== '' ? data.bannerCampaign.description : ''}
								</span>
								{
            data.bannerCampaign.amount === 1000 ?
            <button onClick={() => setIsTermsOn(!isTermsOn)}>
										<InfoIcon className="w-4 h-4 ml-1 -mt-[3px] lg:inline-block" /> 
										</button> :
            null}


							</div>

						</div>
							
					</div>}



				<header className={`py-2 ${isSticky ? 'fixed' : ''} mx-auto top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60`}>
				    <div className="h-6 md:h-0"></div>
					<nav className="container mx-auto px-4">
						<div className="flex flex-wrap items-center justify-between gap-4 sm:flex-nowrap md:gap-8">
							<div className="w-36 md:w-40">
							<Link to="/">
								{/* <div className="font-bold text-2xl bg-[linear-gradient(to_right,theme(colors.indigo.600),theme(colors.indigo.700),theme(colors.sky.600),theme(colors.fuchsia.600))] dark:bg-[linear-gradient(to_right,theme(colors.indigo.400),theme(colors.indigo.100),theme(colors.sky.400),theme(colors.fuchsia.400))] bg-clip-text bg-[length:100%_auto] dark:bg-[length:200%_auto] text-transparent">SuperFD</div>
                  */}
							 <img src={SuperFDLogo} alt="Superfd" className="object-cover" />
							</Link>
							</div>

							{/* <ul className="hidden lg:flex">
              {LINKS.map((link) => (
              	<NavLink name={link.name} key={link.to} to={link.to}>
              		{link.name}
              	</NavLink>
              ))}
              </ul> */}

							<div className="flex items-center gap-2 h-full">

							{/* <div className="hidden md:flex flex-col justify-center items-center">
                
                <div className="relative h-[40px] w-[140px]">
                <a href="https://play.google.com/store/apps/details?id=com.superfd.SuperFD&pcampaignid=web_share" target="_blank" rel="noopener noreferrer" className="h-[60px] w-[180px]">
                <img
                	src={GooglePlayImg}
                	alt="Download on the App Store"
                	className="h-full w-full object-contain"
                />
                </a>
                </div>
                </div> */}


							{user ?
              <div className="flex items-center gap-2">	

									{/* <Button asChild variant="secondary" className="rounded-md p-1">
                  <Link to="/refer_and_earn">
                  	<div className="flex flex-col justify-center items-center">
                  	
                  		<GiftIcon className="h-6 w-6 align-self inline-block" />
                  	
                  		<Badge variant={'max_return'} className='align-self inline-block' >₹250</Badge>
                  		</div>
                  	<span className="hidden md:block ml-2">Refer & Earn</span>
                  </Link>
                  </Button> */}


									{/* <div className="py-2 lg:py-3 flex justify-between align-center">
                  <div className="flex items-center space-x-2">
                  <Switch id="senior-citizen" name="isSenior" checked={true} onCheckedChange={() => {}} />
                  <Label htmlFor="senior-citizen">Senior Citizen</Label>
                  </div>
                  </div> */}

									{/* <GeneralAndSeniorSwitch userIsSenior={
                  (data.isSenior && data.isSenior === 'true') || (data.isSenior && data.isSenior === 'false') 
                  	? data.isSenior === 'true' 
                  		? true 
                  		: false 
                  	: data.user && data.user.age 
                  		? data.user.age > 60 ? true : false 
                  		: true
                  
                  }
                  deviceType={data.requestInfo.deviceType }
                  /> */}
									
									{userIsAdmin ?
                <Button asChild variant="secondary">
											<Link to="/admin">
												<Icon name="backpack">
													<span className="hidden sm:block">Admin</span>
												</Icon>
											</Link>
										</Button> :
                null}

										{/* <Button asChild variant="outline" size="sm">
                  <Link to="/refer_and_earn">
                   <MailPlusIcon />
                  </Link>
                  </Button> */}

									<UserDropdown />
									
								</div> :
              gmailUser ?

              <GamilUserDropdown /> :



              <Button asChild variant="default" size="sm">
											<Link to="/auth">Login/Register</Link>
										</Button>}



							</div>
						</div>
					</nav>
				</header>

				<Dialog open={isTermsOn} onOpenChange={setIsTermsOn}>

				<DialogContent className="text- sm:max-w-[425px]">
					<DialogHeader>
					<DialogTitle className="text-xl">Terms & Conditions</DialogTitle>
					<DialogDescription>Please review our key terms for ₹1,000 Cashback Offer</DialogDescription>
					</DialogHeader>

					<div className="space-y-4 py-4">
					{TERMS_POINTS_CASHBACK.map((point, index) =>
            <div key={index} className="flex gap-3 pb-4 border-b border-border last:border-b-0">
						<div className="flex-shrink-0">
							<div className="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground text-sm font-medium">
							{index + 1}
							</div>
						</div>
						<div className="flex-1 min-w-0">
							<p className="text-xs md:text-sm text-muted-foreground leading-relaxed">{point.description}</p>
						</div>
						</div>
            )}
					</div>

					<div className="flex flex-col gap-4">
					{/* <Button asChild onClick={() => setIsTermsOn(false)} className="flex-1 bg-transparent" variant="outline">
              <Link to="/refer_and_earn">
              	Read all T&C
              </Link>
              </Button> */}
					<Button onClick={() => setIsTermsOn(false)} className="flex-1">
						<Check className="w-4 h-4 mr-2" />Got it
					</Button>
					</div>
				</DialogContent>

				</Dialog>

				<div className="flex-1 mt-6 md:mt-0">
					{
        data.bannerCampaign.isActive ?
        <div className={`${isSticky ? 'block' : 'hidden'} h-[120px]`}></div> :

        <div className={`h-[70px] md:h-[77px]`}></div>}


					<Outlet />
				</div>

				

				<footer className="py-10">
			
			    {
        location.pathname && location.pathname.split('/').length <= 3 && !(location.pathname.split('/')[2] === 'bookedfd') ?

        <div className={`container mx-auto px-4 sm:px-6 lg:px-8 ${data.requestInfo.deviceType === 'MOBILE_APP' ? 'hidden' : 'block'}`}>
						<Separator className="my-5" />
						<div className="w-full flex justify-center md:justify-between items-center py-4 pt-0">
							{/* <div>
              <Link className="block text-xl font-semibold" to="/">
              SuperFD
              </Link>
              </div> */}

							<div className="w-36 md:w-40">
								<Link to="/">
									<img src={SuperFDLogo} alt="Superfd" className="object-cover" />
								</Link>
							</div>
							<div>
							{/* <ThemeSwitchLightOrDark userPreference={data.requestInfo.userPrefs.theme} /> */}
							</div>
						</div>

						<div className="w-full flex flex-col md:flex-row items-center gap-6 md:gap-2 font-bold text-mobile-body md:text-base text-muted-foreground">
							{/* <div>
              <span className="text-sm md:text-base "><CopyrightIcon className="inline-block mr-2" />{'2024 SUPERFD TECHNOLOGIES PVT. LTD.'}</span>
              </div> */}
							<div>
								<Link className="hover:text-primary mr-4" to="/about">
								About Us
								</Link>
								<Link className="hover:text-primary mr-4" to="/support">
								Support
								</Link>
								<Link className="hover:text-primary mr-4" to="/calculator">
								FD Calculator
								</Link>
							</div>
							<div>
								<Link className="hover:text-primary mr-4" to="/blog">
								Blog
								</Link>
                                <Link className="hover:text-primary " to="/privacy-policy">
								 Privacy Policy
								</Link>
							</div>
							
						
						</div>

						<Separator className="my-5" />

						{/* Content Sections */}
						<div className="py-4 space-y-4 text-muted-foreground">
						{/* Fixed Deposit */}
						<div>
							{/* <span className="font-semibold">Fixed Deposit :</span>
                <span className="ml-2">
                Suroday SF Bank || Unity SF Bank || Utkarsh SF Bank || Shriram Finance || Bajaj Finance || Mahindra
                Finance
                </span> */}

							<span className="text-mobile-body md:text-base font-semibold">
								<Link className="hover:text-primary" to="/">
								 Fixed Deposit :
								</Link>
							</span>
							<span className="text-mobile-body md:text-base ml-2">
								<Link className="hover:text-primary mr-2" to="/fd/cmc8t2dur0015c8w0mfg0trca">
								Suryoday SF Bank
								</Link>
								|
								<Link className="hover:text-primary mx-2" to="/fd/cmc8w1is3001lc8w0uqrdu542">
								Unity SF Bank
								</Link>
								|
								<Link className="hover:text-primary mx-2" to="/fd/cmum35lik00ouzia0j1yu8f17">
								DCB Bank
								</Link>
								|
								<Link className="hover:text-primary mx-2" to="/fd/cmdrih74z0002kk055813y9nu">
								Utkarsh SF Bank
								</Link>
								|
								<Link className="hover:text-primary mx-2" to="/fd/cmc8ytv5l001w10ffo5j0zvd9">
								Shriram Finance
								</Link>
								|
								<Link className="hover:text-primary mx-2" to="/fd/cmc90hhex002qc8w0u0ws85fc">
								Bajaj Finance
								</Link>
								|
								<Link className="hover:text-primary mx-2" to="/fd/cmc9208ou003c10fftt0v3vjw">
								Mahindra Finance
								</Link>
								
							</span>
						</div>

						{/* FD Rates */}
						<div>
							{/* <span className="font-semibold">FD Rates :</span>
                <span className="ml-2">
                SBI FD Interest Rates || HDFC FD Interest Rates || ICICI FD Interest Rates || Axis FD Interest Rates ||
                Kotak FD Interest Rates || Indsind FD Interest Rates || Bank of Baroda FD Interest Rates || Canara FD
                Interest Rates || PNB FD Interest Rates || IDBI FD Interest Rates
                </span> */}

							<span className="text-mobile-body md:text-base font-semibold">
								<Link className="hover:text-primary" to="/calculator">
								FD Rates :
								</Link>
							</span>
							<span className="text-mobile-body md:text-base ml-2">
								<Link className="hover:text-primary mr-2" to="/fixed-deposit/sbi-bank-fd-rates">
								SBI FD Interest Rates
								</Link>
								|
								<Link className="hover:text-primary mx-2" to="/fixed-deposit/hdfc-bank-fd-rates">
								HDFC FD Interest Rates
								</Link>
								|
								<Link className="hover:text-primary mx-2" to="/fixed-deposit/icici-bank-fd-rates">
								ICICI FD Interest Rates
								</Link>
								|
								<Link className="hover:text-primary mx-2" to="/fixed-deposit/axis-bank-fd-rates">
								Axis FD Interest Rates 
								</Link>
								|
								<Link className="hover:text-primary mx-2" to="/fixed-deposit/kotak-bank-fd-rates">
								Kotak FD Interest Rates
								</Link>
								|
								<Link className="hover:text-primary mx-2" to="/fixed-deposit/indusind-bank-fd-rates">
								IndusInd FD Interest Rates
								</Link>
								|
								<Link className="hover:text-primary mx-2" to="/fixed-deposit/bank_of_baroda-bank-fd-rates">
								Bank of Baroda FD Interest Rates
								</Link>
								|
								<Link className="hover:text-primary mx-2" to="/fixed-deposit/canara-bank-fd-rates">
								Canara FD Interest Rates
								</Link>
								|
								<Link className="hover:text-primary mx-2" to="/fixed-deposit/pnb-bank-fd-rates">
								PNB FD Interest Rates
								</Link>
								|
								<Link className="hover:text-primary mx-2" to="/fixed-deposit/idbi-bank-fd-rates">
								IDBI FD Interest Rates
								</Link>
								
							</span>


						</div>

						{/* Blogs */}
						<div>
							<span className="text-mobile-body md:text-base font-semibold">
								<Link className="hover:text-primary" to="/blog">
									Blogs :
								</Link>
							</span>
							<span className="text-mobile-body md:text-base ml-2">
								<Link className="hover:text-primary mr-2" to="/blog/fds-are-set-to-be-safer-than-ever">
									DICGC Insurance for FDs
								</Link>
								|
								<Link className="hover:text-primary mx-2" to="/blog/tired-of-hearing-about-the-inr12l-tax-exemption">
								  12L Tax Exemption
								</Link>
								|
								<Link className="hover:text-primary mx-2" to="/blog/are-fd-rates-finally-set-to-drop">
								Repo Rate Changes
								</Link>
								|
								<Link className="hover:text-primary mx-2" to="/blog/small-finance-banks-are-on-the-rise">
								Rising Small Finance Banks
								</Link>
								|
								<Link className="hover:text-primary mx-2" to="/blog/india-still-loves-fds">
								Fixed Deposits India
								</Link>
								|
								<Link className="hover:text-primary mx-2" to="/blog/indian-banks-dominate-the-long-term-fd-market">
								Long vs Short Term FDs
								</Link>
							</span>
						</div>
						</div>

						<div className="text-mobile-body md:text-base text-muted-foreground">
							<span className="font-semibold">Address :</span>
							<span className="ml-2">{`SuperFD Technologies Private Limited, Wework Cherry Hills, Embassy Golf Links BP, Domlur, Bangalore, Karnataka - 560071`}</span>
							
						</div>
						</div> :

        null}
        				
				</footer>

			{/* </div> */}
			<EpicToaster toast={data.toast} />
			<EpicProgress />
		</Document>);

}

const TERMS_POINTS_CASHBACK = [
{
  title: "Acceptance of Terms",
  description:
  "This offer is applicable only on FDs made with Suryoday or Unity Small Finance Bank."
},
// {
//   title: "User Responsibilities",
//   description:
// 	"The minimum booking amount must be ₹20,000 (INR Twenty Thousand) or more to qualify for the offer.",
// },
{
  title: "User Responsibilities",
  description:
  "The minimum booking amount must be ₹1,00,000 (1 Lakh) or more to qualify for the offer."
},
{
  title: "Cashback",
  description:
  "You will receive ₹1,000 cashback within 4 hours."
},
// {
//   title: "Intellectual Property Rights",
//   description:
// 	"The offer is valid for customers making new bookings only. Existing or previous bookings are not eligible.",
// },
// {
//   title: "Limitation of Liability",
//   description:
// 	"Eligible customers will receive a cashback of ₹1,000 within 14 days of successful booking.",
// },
// {
// 	title: "Limitation of Liability",
// 	description:
// 	  "Eligible customers will receive a cashback of ₹1,000 within 2 days of successful booking.",
//   },
{
  title: "Changes to Terms",
  description:
  "You can reach us at support@superfd.in in case of any queries."
}];



function AppWithProviders() {
  const data = useLoaderData<typeof loader>();
  return (
    <AuthenticityTokenProvider token={data.csrfToken}>
			<HoneypotProvider {...data.honeyProps}>
				<GoogleOAuthProvider clientId={data.tarrakkiENV.GOOGLE_OAUTH_CLIENT_ID}>
					<App />
				</GoogleOAuthProvider>
			</HoneypotProvider>
		</AuthenticityTokenProvider>);

}

export default withSentry(AppWithProviders);

function UserDropdown() {
  const data = useLoaderData<typeof loader>();
  const user = useOptionalUser();

  return (
    <DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button variant="secondary" asChild>
					<Link
            to={`/`}
            onClick={(e) => e.preventDefault()}
            className="flex items-center gap-2">

						<img
              className="h-8 w-8 rounded-full object-cover"
              alt={user?.name}
              src={data.profileImageUrl && data.profileImageUrl !== '' ? data.profileImageUrl : getUserImgSrc(user?.image?.id)} />

						<span className="hidden md:block lg:hidden text-body-sm font-bold">
							{user?.name ? user.name.split(' ')[0] : ''}
						</span>
						<span className="hidden lg:block text-body-sm font-bold">
							{user?.name}
						</span>
					</Link>
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuPortal>
				<DropdownMenuContent sideOffset={8} align="start">
					<DropdownMenuItem asChild>
						<Link prefetch="intent" to={`/users/${user?.id}`}>
							<Icon className="text-body-md" name="avatar">
								Profile
							</Icon>
						</Link>
					</DropdownMenuItem>
					<DropdownMenuItem asChild>
						<Link prefetch="intent" to={`/users/bookedfd`}>
							<Icon className="text-body-md" name="id-card">
								Booked FD
							</Icon>
						</Link>
					</DropdownMenuItem>
					<DropdownMenuItem asChild>
						<Link prefetch="intent" to={`/users/portfolio`}>
							<Icon className="text-body-md" name="card-stack">
								Track External FD
							</Icon>
						</Link>
					</DropdownMenuItem>
				</DropdownMenuContent>
			</DropdownMenuPortal>
		</DropdownMenu>);

}

function GamilUserDropdown() {
  const gmailUser = useOptionalGmailUser();

  return (
    <DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button variant="secondary" asChild>
					<Link
            to={`/`}
            onClick={(e) => e.preventDefault()}
            className="flex items-center gap-2">

						<img
              className="h-8 w-8 rounded-full object-cover"
              alt={gmailUser?.name}
              src={gmailUser?.picture} />

						<span className="hidden md:block lg:hidden text-body-sm font-bold">
							{gmailUser?.name ? gmailUser.name.split(' ')[0] : ''}
						</span>
						<span className="hidden lg:block text-body-sm font-bold">
							{gmailUser?.name}
						</span>
					</Link>
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuPortal>
				<DropdownMenuContent sideOffset={8} align="start">
					<DropdownMenuItem asChild>
						<Link prefetch="intent" to={`/users/gmail/${gmailUser?.id}`}>
							<Icon className="text-body-md" name="avatar">
								Profile
							</Icon>
						</Link>
					</DropdownMenuItem>
					{/* <DropdownMenuItem asChild>
            <Link prefetch="intent" to={`/users/bookedfd`}>
            	<Icon className="text-body-md" name="id-card">
            		Booked FD
            	</Icon>
            </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
            <Link prefetch="intent" to={`/users/portfolio`}>
            	<Icon className="text-body-md" name="card-stack">
            		FD Portfolio
            	</Icon>
            </Link>
            </DropdownMenuItem> */}
				</DropdownMenuContent>
			</DropdownMenuPortal>
		</DropdownMenu>);

}

/**
 * @returns the user's theme preference, or the client hint theme if the user
 * has not set a preference.
 */
// export function useTheme() {
// 	const hints = useHints()
// 	const requestInfo = useRequestInfo()
// 	const optimisticMode = useOptimisticThemeMode()
// 	if (optimisticMode) {
// 		return optimisticMode === 'system' ? hints.theme : optimisticMode
// 	}
// 	// for Auto pickup color preference theme 
// 	return requestInfo.userPrefs.theme ?? hints.theme
// }

export function useThemeLightOrDark() {
  const requestInfo = useRequestInfo();
  const optimisticMode = useOptimisticThemeModeLightOrDark();
  if (optimisticMode) {
    return optimisticMode;
  }
  // for Auto pickup color preference theme 
  return requestInfo.userPrefs.theme;
}

/**
 * If the user's changing their theme mode preference, this will return the
 * value it's being changed to.
 */
// export function useOptimisticThemeMode() {
// 	const fetchers = useFetchers()
// 	const themeFetcher = fetchers.find(f => f.formAction === '/')

// 	if (themeFetcher && themeFetcher.formData) {
// 		const submission = parse(themeFetcher.formData, {
// 			schema: ThemeFormSchema,
// 		})
// 		return submission.value?.theme
// 	}
// }

export function useOptimisticThemeModeLightOrDark() {
  const fetchers = useFetchers();
  const themeFetcher = fetchers.find((f) => f.formAction === '/');

  if (themeFetcher && themeFetcher.formData) {
    const submission = parse(themeFetcher.formData, {
      schema: ThemeFormSchemaLightOrDark
    });
    return submission.value?.theme;
  }
}

// function ThemeSwitch({ userPreference }: { userPreference?: Theme | null }) {
// 	const fetcher = useFetcher<typeof action>()

// 	const [form] = useForm({
// 		id: 'theme-switch',
// 		lastSubmission: fetcher.data?.submission,
// 	})

// 	const optimisticMode = useOptimisticThemeMode()
// 	const mode = optimisticMode ?? userPreference ?? 'system'
// 	const nextMode =
// 		mode === 'system' ? 'light' : mode === 'light' ? 'dark' : 'system'
// 	const modeLabel = {
// 		light: (
// 			<Icon name="sun">
// 				<span className="sr-only">Light</span>
// 			</Icon>
// 		),
// 		dark: (
// 			<Icon name="moon">
// 				<span className="sr-only">Dark</span>
// 			</Icon>
// 		),
// 		system: (
// 			<Icon name="laptop">
// 				<span className="sr-only">System</span>
// 			</Icon>
// 		),
// 	}

// 	return (
// 		<fetcher.Form method="POST" {...form.props}>
// 			<input type="hidden" name="theme" value={nextMode} />
// 			<div className="flex gap-2">
// 				<button
// 					type="submit"
// 					className="flex h-8 w-8 cursor-pointer items-center justify-center"
// 				>
// 					{modeLabel[mode]}
// 				</button>
// 			</div>
// 			<ErrorList errors={form.errors} id={form.errorId} />
// 		</fetcher.Form>
// 	)
// }

// export function ThemeSwitchLightOrDark({ userPreference }: { userPreference?: Theme | null }) {
// 	const fetcher = useFetcher<typeof action>()

// 	const [form] = useForm({
// 		id: 'theme-switch-light-dark',
// 		lastSubmission: fetcher.data?.submission,
// 	})

// 	const optimisticMode = useOptimisticThemeModeLightOrDark()
// 	const mode = optimisticMode ?? userPreference ?? 'dark'
// 	const nextMode =
// 		mode === 'dark' ? 'light' : 'dark' 
// 	const modeLabel = {
// 		light: (
// 			<Icon name="sun">
// 				<span className="sr-only">Light</span>
// 			</Icon>
// 		),
// 		dark: (
// 			<Icon name="moon">
// 				<span className="sr-only">Dark</span>
// 			</Icon>
// 		)
// 	}

// 	return (
// 		<fetcher.Form method="POST" {...form.props}>
// 			<input type="hidden" name="theme" value={nextMode} />
// 			<div className="flex gap-2">
// 				<button
// 					type="submit"
// 					className="flex h-8 w-8 cursor-pointer items-center justify-center"
// 				>
// 					{modeLabel[mode]}
// 				</button>
// 			</div>
// 			<ErrorList errors={form.errors} id={form.errorId} />
// 		</fetcher.Form>
// 	)
// }

// function GeneralAndSeniorSwitch({ userIsSenior, deviceType }: { userIsSenior: boolean, deviceType: string }) {
// 	const fetcher = useFetcher<typeof action>()
// 	const submit = useSubmit()

// 	const [form] = useForm({
// 		id: 'isSenior-switch',
// 	})

// 	const mode = userIsSenior ?? true

// 	return (
// 		<fetcher.Form method="POST" {...form.props}>

// 			<div className="py-2 lg:py-3 flex justify-between align-center">
// 			<div className="flex items-center space-x-2">
// 				<Switch id="senior-citizen-root" name="senior-citizen-root" checked={mode} onCheckedChange={
// 					(e) => {
// 						submit({ isSenior: !mode, formId: IS_SENIOR_FORM_ID }, { method: "post" });

// 						return !mode
// 					}
// 				} />
// 				<Label htmlFor="senior-citizen-root">{`${deviceType.includes('MOBILE') ? 'Senior' : 'Senior Citizen'}`}</Label>
// 			</div>
// 			</div>

// 			<ErrorList errors={form.errors} id={form.errorId} />
// 		</fetcher.Form>
// 	)
// }

export function ErrorBoundary() {
  // the nonce doesn't rely on the loader so we can access that
  const nonce = useNonce();

  // NOTE: you cannot use useLoaderData in an ErrorBoundary because the loader
  // likely failed to run so we have to do the best we can.
  // We could probably do better than this (it's possible the loader did run).
  // This would require a change in Remix.

  // Just make sure your root route never errors out and you'll always be able
  // to give the user a better UX.

  return (
    <Document nonce={nonce}>
			<GeneralErrorBoundary />
		</Document>);

}


// function NavLink({
// 	to,
// 	name,
// 	...rest
// }: Omit<Parameters<typeof Link>['0'], 'to'> & { to: string, name: string }) {
// 	const location = useLocation()
// 	const isSelected =
// 		to === location.pathname || location.pathname.startsWith(`${to}/`)

// 	return (
// 		<li className="px-5 py-2">
// 			<Link 
// 			prefetch="intent"
// 			className={clsx(
// 				'underlined block whitespace-nowrap text-lg  font-medium hover:text-primary focus:text-primary focus:outline-none',
// 				{
// 					'active text-primary': isSelected,
// 					'text-muted-foreground': !isSelected,
// 				},
// 			)}
// 			to={to}
// 			{...rest}
// 			>
// 			 {name}
// 			</Link>
// 		</li>
// 	)
// }